import { useState, useEffect, useRef } from "react";
import { ChevronDown, Star, Pencil } from "lucide-react";
import { Button } from "../../../../../components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../../components/ui/tooltip";
import { Citation } from "../../../../../models/audit/citation";
import { type CitationGroup } from "../../../../../models/audit/attributeTestColumn";
import { type BoundingBoxState } from "./types";
import { type AuditSession } from "../../../../../models/audit/auditSession";
import { getGroupLetter } from "../../../../../lib/groupLabels";

interface ReperformanceStepsPanelProps {
  reperformanceSteps: AuditSession["sourceRows"][number]["attributeTestResults"][number]["reperformanceSteps"];
  focusedStepId: string | undefined;
  currentBoundingBox: BoundingBoxState | undefined;
  onStepToggle: (stepId: string | undefined) => void;
  onCitationClick: (citation: Citation) => void;
  // Selects a citation without counting it as a user pick. Used when the panel
  // picks the first citation itself; defaults to `onCitationClick`.
  onAutoSelectCitation?: (citation: Citation) => void;
  onEditCitationSummary?: (citation: Citation) => void;
}

function CitationItem({
  citation,
  label,
  isSelected,
  onCitationClick,
  onEditCitationSummary,
}: {
  citation: Citation;
  label: string;
  isSelected: boolean;
  onCitationClick: (citation: Citation) => void;
  onEditCitationSummary?: (citation: Citation) => void;
}) {
  return (
    <button
      className={`w-full text-left border rounded-md px-3 py-2 transition-[color,background-color,border-color,opacity] ${
        isSelected
          ? "bg-blue-50 border-blue-300"
          : "bg-white border-stone-200 hover:bg-stone-50 opacity-50"
      }`}
      onClick={(e) => {
        e.stopPropagation();
        onCitationClick(citation);
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-xs text-font-secondary font-mono mb-0.5">
            {label}
          </div>
          <div
            className={`text-sm ${
              isSelected ? "whitespace-normal break-words" : "truncate"
            }`}
          >
            {citation.shortSummary ||
              `Citation ${citation.citationOrder || ""}`}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0 mt-2">
          {onEditCitationSummary && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditCitationSummary(citation);
                    }}
                  >
                    <Pencil className="h-3 w-3 text-stone-400" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Edit summary</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <Star
            className={`h-4 w-4 ${
              citation.isKey === true
                ? "fill-amber-400 text-amber-400"
                : "text-stone-300"
            }`}
          />
        </div>
      </div>
    </button>
  );
}

function CitationGroupItem({
  group,
  stepLabel,
  groupIndex,
  isExpanded,
  hasActiveSelection,
  currentBoundingBox,
  onToggle,
  onCitationClick,
  onEditCitationSummary,
}: {
  group: CitationGroup;
  stepLabel: string;
  groupIndex: number;
  isExpanded: boolean;
  hasActiveSelection: boolean;
  currentBoundingBox: BoundingBoxState | undefined;
  onToggle: () => void;
  onCitationClick: (citation: Citation) => void;
  onEditCitationSummary?: (citation: Citation) => void;
}) {
  const groupLetter = getGroupLetter(groupIndex);
  const groupLabel = `${stepLabel}.${groupLetter}`;

  return (
    <div>
      <button
        className={`w-full text-left py-2 px-2 rounded text-sm transition-[color,background-color,border-color,opacity] flex items-start gap-2 border-l-4 ${
          isExpanded
            ? "bg-stone-50 font-medium border-blue-500"
            : `border-stone-200 hover:bg-stone-50 ${
                hasActiveSelection ? "opacity-50" : ""
              }`
        }`}
        onClick={onToggle}
      >
        <span className="flex-1 min-w-0">
          <span className="text-font-secondary font-mono">{groupLabel}</span>{" "}
          {group.shortSummary || group.groupName}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 mt-0.5 text-stone-500 ${
            isExpanded ? "" : "rotate-180"
          }`}
        />
      </button>
      {isExpanded && group.citations.length > 0 && (
        <div className="flex flex-col gap-2 ml-3 pl-3 pt-2 mb-2 border-l border-stone-200">
          {group.citations.map((citation, citIndex) => {
            const citLabel = `${groupLabel}.${citIndex + 1}`;
            const isSelected = currentBoundingBox?.citationId === citation.id;
            return (
              <CitationItem
                key={citation.id}
                citation={citation}
                label={citLabel}
                isSelected={isSelected}
                onCitationClick={onCitationClick}
                onEditCitationSummary={onEditCitationSummary}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export function ReperformanceStepsPanel({
  reperformanceSteps,
  focusedStepId,
  currentBoundingBox,
  onStepToggle,
  onCitationClick,
  onAutoSelectCitation,
  onEditCitationSummary,
}: ReperformanceStepsPanelProps) {
  const [expandedGroupId, setExpandedGroupId] = useState<string | undefined>(
    undefined,
  );

  // Keep refs to avoid re-firing the effect on every citation mutation
  const onCitationClickRef = useRef(onCitationClick);
  const onAutoSelectCitationRef = useRef(
    onAutoSelectCitation ?? onCitationClick,
  );
  useEffect(() => {
    onCitationClickRef.current = onCitationClick;
    onAutoSelectCitationRef.current = onAutoSelectCitation ?? onCitationClick;
  });
  // Only a step-header toggle makes the fallback selection a user pick.
  const stepToggledRef = useRef(false);
  const handleStepToggle = (stepId: string | undefined) => {
    // `step.id` is optional, so a header click can leave `focusedStepId`
    // unchanged; the effect then won't run to clear the flag.
    stepToggledRef.current = stepId !== focusedStepId;
    onStepToggle(stepId);
  };
  const reperformanceStepsRef = useRef(reperformanceSteps);
  useEffect(() => {
    reperformanceStepsRef.current = reperformanceSteps;
  });

  // Expand the citation group that owns the currently-selected citation;
  // fall back to the first group (and auto-select its first citation) when
  // there is no external selection to honor.
  useEffect(() => {
    const stepToggled = stepToggledRef.current;
    stepToggledRef.current = false;
    if (!focusedStepId) {
      setExpandedGroupId(undefined);
      return;
    }
    const focusedStep = reperformanceStepsRef.current.find(
      (step) => step.id === focusedStepId,
    );
    const sortedGroups = [...(focusedStep?.citationGroups ?? [])].sort(
      (a, b) => a.groupOrder - b.groupOrder,
    );

    const selectedCitationId = currentBoundingBox?.citationId;
    const groupForSelection = selectedCitationId
      ? sortedGroups.find((g) =>
          g.citations.some((c) => c.id === selectedCitationId),
        )
      : undefined;

    if (groupForSelection) {
      setExpandedGroupId(groupForSelection.id);
      return;
    }

    const firstGroup = sortedGroups[0];
    setExpandedGroupId(firstGroup?.id);
    if (firstGroup && firstGroup.citations.length > 0) {
      const select = stepToggled
        ? onCitationClickRef.current
        : onAutoSelectCitationRef.current;
      select(firstGroup.citations[0]);
    }
  }, [focusedStepId, currentBoundingBox?.citationId]);

  if (reperformanceSteps.length === 0) {
    return null;
  }

  const hasAnyCitationGroups = reperformanceSteps.some(
    (step) => step.citationGroups && step.citationGroups.length > 0,
  );

  if (!hasAnyCitationGroups) {
    return (
      <div>
        <div className="text-sm text-font-secondary font-mono mb-2">
          Steps Taken
        </div>
        <div className="space-y-2">
          {reperformanceSteps.map((step, index) => {
            const isExpanded = focusedStepId === step.id;
            return (
              <div
                key={step.id || index}
                className={`border overflow-hidden ${
                  isExpanded ? "ring ring-stone-500" : "border-stone-200"
                }`}
              >
                <button
                  className="w-full p-3 flex items-start justify-between gap-2 hover:bg-stone-50 transition-colors"
                  onClick={() => {
                    handleStepToggle(isExpanded ? undefined : step.id);
                  }}
                >
                  <div className="flex-1 text-left">
                    <div className="font-medium text-sm">
                      {index + 1}. {step.stepName}
                    </div>
                  </div>
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 text-font-secondary ${
                      isExpanded ? "" : "rotate-180"
                    }`}
                  />
                </button>
                {isExpanded && (
                  <div className="px-3 pb-3 space-y-2">
                    <div className="text-sm text-font-secondary">
                      {step.stepDescription}
                    </div>
                    {step.citations.length > 0 && (
                      <div className="flex flex-col gap-2 mt-4">
                        {step.citations.map((citation) => {
                          const isSelected =
                            currentBoundingBox?.citationId === citation.id;
                          return (
                            <div
                              key={citation.id}
                              className="flex items-center gap-1"
                            >
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant={
                                        isSelected ? "default" : "outline"
                                      }
                                      size="sm"
                                      className={`text-xs h-auto min-h-[28px] py-3 flex-1 justify-start text-left bg-stone-50 ${
                                        isSelected
                                          ? "bg-blue-100 text-stone-950 hover:bg-blue-200"
                                          : ""
                                      }`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onCitationClick(citation);
                                      }}
                                    >
                                      <span className="whitespace-normal break-words">
                                        {citation.shortSummary ||
                                          `Citation ${citation.citationOrder || ""}`}
                                      </span>
                                      {citation.isKey === true && (
                                        <Star className="h-3 w-3 ml-1 shrink-0 fill-amber-400 text-amber-400" />
                                      )}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="max-w-xs">
                                      {citation.shortSummary || "No summary"}
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              {onEditCitationSummary && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 w-7 p-0 shrink-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onEditCitationSummary(citation);
                                        }}
                                      >
                                        <Pencil className="h-3 w-3 text-stone-500" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Edit summary</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="text-sm text-font-secondary font-mono mb-2">
        Tickmarks
      </div>
      <div className="space-y-2">
        {reperformanceSteps.map((step, index) => {
          const isExpanded = focusedStepId === step.id;
          const stepLabel = `TM ${index + 1}`;

          return (
            <div
              key={step.id || index}
              className={`border overflow-hidden ${
                isExpanded ? "ring ring-stone-500" : "border-stone-200"
              }`}
            >
              <button
                className="w-full p-3 flex items-start justify-between gap-2 hover:bg-stone-50 transition-colors"
                onClick={() => {
                  handleStepToggle(isExpanded ? undefined : step.id);
                }}
              >
                <div className="flex-1 text-left">
                  <div className="font-medium text-sm flex items-start gap-2">
                    <span className="text-font-secondary font-mono shrink-0">
                      {stepLabel}
                    </span>
                    <span>{step.stepName}</span>
                  </div>
                </div>
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-font-secondary ${
                    isExpanded ? "" : "rotate-180"
                  }`}
                />
              </button>
              {isExpanded && (
                <div className="px-3 pb-3 space-y-2">
                  <div className="text-sm text-font-secondary">
                    {step.stepDescription}
                  </div>

                  {step.citationGroups && step.citationGroups.length > 0 ? (
                    <div className="flex flex-col gap-1 mt-4">
                      {[...step.citationGroups]
                        .sort((a, b) => a.groupOrder - b.groupOrder)
                        .map((group, groupIndex) => (
                          <CitationGroupItem
                            key={group.id}
                            group={group}
                            stepLabel={stepLabel}
                            groupIndex={groupIndex}
                            isExpanded={expandedGroupId === group.id}
                            hasActiveSelection={expandedGroupId !== undefined}
                            currentBoundingBox={currentBoundingBox}
                            onToggle={() => {
                              const newlyExpanded =
                                expandedGroupId !== group.id;
                              setExpandedGroupId(
                                newlyExpanded ? group.id : undefined,
                              );
                              if (newlyExpanded && group.citations.length > 0) {
                                onCitationClick(group.citations[0]);
                              }
                            }}
                            onCitationClick={onCitationClick}
                            onEditCitationSummary={onEditCitationSummary}
                          />
                        ))}
                    </div>
                  ) : (
                    step.citations.length > 0 && (
                      <div className="flex flex-col gap-2 mt-4">
                        {step.citations.map((citation) => {
                          const isSelected =
                            currentBoundingBox?.citationId === citation.id;
                          return (
                            <div
                              key={citation.id}
                              className="flex items-center gap-1"
                            >
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant={
                                        isSelected ? "default" : "outline"
                                      }
                                      size="sm"
                                      className={`text-xs h-auto min-h-[28px] py-3 flex-1 justify-start text-left bg-stone-50 ${
                                        isSelected
                                          ? "bg-blue-100 text-stone-950 hover:bg-blue-200"
                                          : ""
                                      }`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onCitationClick(citation);
                                      }}
                                    >
                                      <span className="whitespace-normal break-words">
                                        {citation.shortSummary ||
                                          `Citation ${citation.citationOrder || ""}`}
                                      </span>
                                      {citation.isKey === true && (
                                        <Star className="h-3 w-3 ml-1 shrink-0 fill-amber-400 text-amber-400" />
                                      )}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="max-w-xs">
                                      {citation.shortSummary || "No summary"}
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              {onEditCitationSummary && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 w-7 p-0 shrink-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onEditCitationSummary(citation);
                                        }}
                                      >
                                        <Pencil className="h-3 w-3 text-stone-500" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Edit summary</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
